import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Spin, Space, Tabs, Table, InputNumber } from 'antd';
import { getPlanConfig, updatePlanConfig } from '../../api/system';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const SubscriptionScreen = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [planConfig, setPlanConfig] = useState(null);
    const [planJson, setPlanJson] = useState({ plans: [], features: [], prices: [] });
    const [initialPlanJson, setInitialPlanJson] = useState({ plans: [], features: [], prices: [] });

    useEffect(() => {
        fetchPlanConfig();
    }, []);

    const fetchPlanConfig = () => {
        setLoading(true);
        getPlanConfig().then(res => {
            if (res) {
                setPlanConfig(res);
                form.setFieldsValue({
                    name: res.name,
                    titleHeadline: res.titleHeadline
                });
                try {
                    const parsed = JSON.parse(res.planJson);
                    setPlanJson(parsed);
                    setInitialPlanJson(JSON.parse(JSON.stringify(parsed))); // 深拷贝保存初始数据
                } catch (error) {
                    console.error('Failed to parse planJson:', error);
                    setPlanJson({ plans: [], features: [], prices: [] });
                    setInitialPlanJson({ plans: [], features: [], prices: [] });
                }
            }
        }).catch(err => {
            message.error('Failed to fetch plan config');
            console.error(err);
        }).finally(() => setLoading(false));
    };

    const handleSubmit = (values) => {
        setSubmitting(true);
        updatePlanConfig({
            name: values.name,
            titleHeadline: values.titleHeadline,
            planJson: JSON.stringify(planJson)
        }).then(res => {
            if (res) {
                message.success('Plan config updated successfully');
                setPlanConfig(res);
            }
        }).catch(err => {
            message.error('Failed to update plan config');
            console.error(err);
        }).finally(() => setSubmitting(false));
    };

    const plansColumns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPlans = [...planJson.plans];
                        newPlans[index].key = e.target.value;
                        setPlanJson({ ...planJson, plans: newPlans });
                    }}
                />
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPlans = [...planJson.plans];
                        newPlans[index].name = e.target.value;
                        setPlanJson({ ...planJson, plans: newPlans });
                    }}
                />
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record, index) => (
                <Button
                    type='link'
                    danger
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        const newPlans = planJson.plans.filter((_, i) => i !== index);
                        setPlanJson({ ...planJson, plans: newPlans });
                    }}
                />
            )
        }
    ];

    const pricesColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 100,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].name = e.target.value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: 100,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].key = e.target.value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Months',
            dataIndex: 'months',
            key: 'months',
            render: (text, record, index) => (
                <InputNumber
                    value={text}
                    onChange={(value) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].months = value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Stripe Price ID',
            dataIndex: 'stripePriceId',
            key: 'stripePriceId',
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].stripePriceId = e.target.value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Apple Product ID',
            dataIndex: 'appleProductId',
            key: 'appleProductId',
            render: (text, record, index) => (
                <Input
                    value={text || ''}
                    onChange={(e) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].appleProductId = e.target.value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Google Product ID / Plan ID',
            key: 'googleIds',
            render: (_, record, index) => (
                <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                    <div>
                        <label style={{ fontSize: 12, color: '#666' }}>Product ID:</label>
                        <Input
                            value={record.googleProductId || ''}
                            onChange={(e) => {
                                const newPrices = [...planJson.prices];
                                newPrices[index].googleProductId = e.target.value;
                                setPlanJson({ ...planJson, prices: newPrices });
                            }}
                            size='small'
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, color: '#666' }}>Plan ID:</label>
                        <Input
                            value={record.googlePlanId || ''}
                            onChange={(e) => {
                                const newPrices = [...planJson.prices];
                                newPrices[index].googlePlanId = e.target.value;
                                setPlanJson({ ...planJson, prices: newPrices });
                            }}
                            size='small'
                        />
                    </div>
                </div>
            )
        },
        {
            title: 'Tips (JSON)',
            dataIndex: 'tips',
            key: 'tips',
            render: (text, record, index) => (
                <Input.TextArea
                    value={Array.isArray(text) ? JSON.stringify(text, null, 2) : '[]'}
                    onChange={(e) => {
                        try {
                            const parsed = JSON.parse(e.target.value);
                            const newPrices = [...planJson.prices];
                            newPrices[index].tips = parsed;
                            setPlanJson({ ...planJson, prices: newPrices });
                        } catch (error) {
                            console.error('Invalid JSON');
                        }
                    }}
                    rows={3}
                />
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record, index) => (
                <Button
                    type='link'
                    danger
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        const newPrices = planJson.prices.filter((_, i) => i !== index);
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        }
    ];

    return (
        <div className='main-wrapper'>
            <div className='cnt-item'>
                <Card title='Subscription Plan Config' loading={loading}>
                    <Spin spinning={loading}>
                        <Form
                            form={form}
                            layout='vertical'
                            onFinish={handleSubmit}
                        >
                            <Form.Item
                                name='name'
                                label='Plan Name'
                                rules={[{ required: true, message: 'Please enter plan name' }]}
                            >
                                <Input placeholder='Enter plan name' />
                            </Form.Item>

                            <Form.Item
                                name='titleHeadline'
                                label='Title Headline'
                                rules={[{ required: true, message: 'Please enter title headline' }]}
                            >
                                <Input placeholder='Enter title headline' />
                            </Form.Item>

                            <Card title='Plan JSON Details' style={{ marginTop: 24 }}>
                                <Tabs
                                    items={[
                                        {
                                            label: 'Plans',
                                            key: 'plans',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={plansColumns}
                                                        dataSource={planJson.plans}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                    />
                                                    <Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setPlanJson({
                                                                ...planJson,
                                                                plans: [...planJson.plans, { key: '', name: '' }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Plan
                                                    </Button>
                                                </div>
                                            )
                                        },
                                        {
                                            label: 'Prices',
                                            key: 'prices',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={pricesColumns}
                                                        dataSource={planJson.prices}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                        scroll={{ x: 1200 }}
                                                    />
                                                    <Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setPlanJson({
                                                                ...planJson,
                                                                prices: [...planJson.prices, { name: '', key: '', months: 1, stripePriceId: '', tips: [] }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Price
                                                    </Button>
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </Card>

                            <Form.Item style={{ marginTop: 24 }}>
                                <Space>
                                    <Button type='primary' htmlType='submit' loading={submitting}>
                                        Save
                                    </Button>
                                    <Button onClick={() => {
                                        form.setFieldsValue({
                                            name: planConfig?.name,
                                            titleHeadline: planConfig?.titleHeadline
                                        });
                                        setPlanJson(JSON.parse(JSON.stringify(initialPlanJson)));
                                    }}>
                                        Reset
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Spin>
                </Card>
            </div>
        </div>
    );
};

export default SubscriptionScreen;